import { Route } from 'react-router-dom';

import { AuthGuard } from 'components/AuthGuard';
import { MainLayout } from 'components/Layout';
import { DriverRoutes } from 'features/drivers/routes';
import { AsyncStatusRoutes } from 'features/operations';
import { lazyImport } from 'utils/lazyImport';

import { AdminRouteGuard } from './AdminRouteGuard';
import { AppRouteConfig } from './appRouteConfig';
import { CustomerRouteGuard } from './CustomerRouteGuard';
import { NotificationSystemGuard } from './NotificationSystemGuard';

const { AddressBooks } = lazyImport(
  () => import('features/records/routes/customers/AddressBooks'),
  'AddressBooks'
);

const { CustomerPortalConfiguration } = lazyImport(
  () =>
    import(
      'features/customerPortal/components/CustomerPortalConfiguration/CustomerPortalConfiguration'
    ),
  'CustomerPortalConfiguration'
);
const { DegreeDaysRoutes } = lazyImport(
  () => import('features/degreeDays/routes/DegreeDaysRoutes'),
  'DegreeDaysRoutes'
);
const { TankMonitorV2Routes } = lazyImport(
  () => import('features/tankMonitor-v2/routes'),
  'TankMonitorV2Routes'
);

const { OperationsRoutes } = lazyImport(() => import('features/operations'), 'OperationsRoutes');

const { AllCustomerAssets } = lazyImport(
  () => import('features/records/routes/customers/AllCustomerAssets'),
  'AllCustomerAssets'
);
const { SettingRoutes } = lazyImport(() => import('features/settings/routes'), 'SettingRoutes');
const { NotificationTemplateEditorPage } = lazyImport(
  () => import('features/notificationSystem/routes/NotificationTemplateEditorPage'),
  'NotificationTemplateEditorPage'
);
const { NotificationSystemPage } = lazyImport(
  () => import('features/notificationSystem/routes/NotificationSystemPage'),
  'NotificationSystemPage'
);
// const { TankMonitorRoutes } = lazyImport(
//   () => import('features/tankMonitor/routes'),
//   'TankMonitorRoutes'
// );

const { DataImportRoutes } = lazyImport(
  () => import('features/dataImport/routes'),
  'DataImportRoutes'
);
const { RecordRoutes } = lazyImport(() => import('features/records'), 'RecordRoutes');
const { PlaceholderRoutes } = lazyImport(() => import('features/placeholder'), 'PlaceholderRoutes');
const { ShiftRoutes } = lazyImport(() => import('features/routing'), 'ShiftRoutes');
const { OrdersRoutes } = lazyImport(() => import('features/orders'), 'OrdersRoutes');
const { DashboardRoutes } = lazyImport(() => import('features/dashboard.new'), 'DashboardRoutes');
const { DeliveryOrders } = lazyImport(
  () => import('features/dashboard.new/components/DeliveryOrders'),
  'DeliveryOrders'
);
const { RecurringOrderPage } = lazyImport(
  () => import('features/dashboard.new/pages/RecurringOrderPage'),
  'RecurringOrderPage'
);
const { ShiftBoardRoutes } = lazyImport(() => import('features/shifts'), 'ShiftBoardRoutes');
const { ProfileRoutes } = lazyImport(() => import('features/profile'), 'ProfileRoutes');
const { StaffRoutes } = lazyImport(() => import('features/staff'), 'StaffRoutes');
const { LeaseEquipmentOrderFormPage } = lazyImport(
  () => import('features/leaseEquipmentOrder/pages/LeaseEquipmentOrderFormPage'),
  'LeaseEquipmentOrderFormPage'
);

const { Graph } = lazyImport(
  () => import('features/dashboard.new/components/Graph/Graph'),
  'Graph'
);
const { PricingRoutes } = lazyImport(() => import('features/pricing'), 'PricingRoutes');
const { Dashboard } = lazyImport(() => import('javascript/containers/Dashboard'), 'Dashboard');
const { QrCodeList } = lazyImport(
  () => import('javascript/containers/dispatch/qr_codes'),
  'QrCodeList'
);
const { ModalTypeDetailPage } = lazyImport(
  () => import('javascript/containers/Shifts/components/Modal/ModalTypeDetailPage'),
  'ModalTypeDetailPage'
);

const { LeaRoute } = lazyImport(() => import('features/operations/routes/LeaRoute'), 'LeaRoute');

const { LeaMovementOrderPage } = lazyImport(
  () => import('features/operations/routes/LEA.new/LeaMovementOrderPage'),
  'LeaMovementOrderPage'
);

const { DispatchRoutes } = lazyImport(() => import('features/dispatch'), 'DispatchRoutes');

const { ReportRoutes } = lazyImport(() => import('features/reports/routes'), 'ReportRoutes');

const { DeliveryTypeOrderDetailPage } = lazyImport(
  () => import('features/orderDetails/pages/DeliveryTypeOrderDetailPage'),
  'DeliveryTypeOrderDetailPage'
);

const { ExtractionOrderDetailsPage } = lazyImport(
  () => import('features/orderDetails/pages/ExtractionOrderDetailsPage'),
  'ExtractionOrderDetailsPage'
);

const { UpdateDeliveryOrderPage } = lazyImport(
  () => import('features/orderDetails/pages/UpdateDeliveryOrderPage'),
  'UpdateDeliveryOrderPage'
);

const { PandaBoxVersion } = lazyImport(
  () => import('features/records/routes/self-customer/PandaBox/PandaBoxVersion'),
  'PandaBoxVersion'
);
const { MobileVersion } = lazyImport(
  () => import('features/records/routes/self-customer/PandaBox/MobileVersion'),
  'MobileVersion'
);
const { PandaBoxLogs } = lazyImport(
  () => import('features/records/routes/self-customer/PandaBox/PandaBoxLogs/PandaBoxLogs'),
  'PandaBoxLogs'
);

const ORDER_DETAILS = {
  delivery: <DeliveryTypeOrderDetailPage />,
  extraction: <ExtractionOrderDetailsPage />,
};

export const privateRoutes = (
  <Route path="*" element={<AuthGuard />}>
    <Route path="*" element={<MainLayout />}>
      <Route
        key={'detailModalFromDelivery'}
        path={`shift-details/:id`}
        element={<ModalTypeDetailPage />}
      />
      {(['delivery'] as const).map((path) => {
        return <Route key={path} path={`${path}/:id`} element={ORDER_DETAILS[path]} />;
      })}
      {(['extraction'] as const).map((path) => {
        return <Route key={path} path={`${path}/:id`} element={ORDER_DETAILS[path]} />;
      })}
      <Route path="update-delivery-order/:id" element={<UpdateDeliveryOrderPage />} />
      {(['customers', 'self_customer'] as const).map((path) => (
        <Route key={path} path={`${path}/*`} element={<RecordRoutes which={path} />} />
      ))}
      {(['route_config', 'lea_old'] as const).map((path) => (
        <Route key={path} path={`${path}/*`} element={<OperationsRoutes which={path} />} />
      ))}
      <Route path="lea/*" element={<LeaRoute />} />
      <Route path="lem/:id" element={<LeaMovementOrderPage />} />
      {(['async_status'] as const).map((path) => (
        <Route key={path} path={`${path}/*`} element={<AsyncStatusRoutes which={path} />} />
      ))}
      <Route path="" element={<Dashboard />} />
      <Route path={AppRouteConfig.DRIVERS_TIME_SPENT.path} element={<DriverRoutes />} />
      <Route path="dispatch/*" element={<DispatchRoutes />} />
      <Route path="orders/*" element={<OrdersRoutes />} />
      <Route path="data-import/*" element={<DataImportRoutes />} />
      <Route path="pricing/*" element={<PricingRoutes />} />
      <Route
        path="notification-system"
        element={
          <AdminRouteGuard>
            <NotificationSystemGuard>
              <NotificationSystemPage />
            </NotificationSystemGuard>
          </AdminRouteGuard>
        }
      />
      <Route
        path="notification-system/template/:templateId"
        element={
          <AdminRouteGuard>
            <NotificationSystemGuard>
              <NotificationTemplateEditorPage />
            </NotificationSystemGuard>
          </AdminRouteGuard>
        }
      />
      <Route path="lease-equipment-order" element={<LeaseEquipmentOrderFormPage />} />
      <Route path="customer_assets" element={<AllCustomerAssets />} />
      <Route path="point_of_contacts" element={<AddressBooks />} />
      <Route path="shift_plans/*" element={<ShiftRoutes />} />
      <Route path="reports/*" element={<ReportRoutes />} />
      <Route
        path="settings/*"
        element={
          <AdminRouteGuard>
            <SettingRoutes />
          </AdminRouteGuard>
        }
      />
      <Route path="dashboard/*" element={<Dashboard />} />

      <Route
        path="profile/*"
        element={
          <CustomerRouteGuard>
            <ProfileRoutes />
          </CustomerRouteGuard>
        }
      />
      {/* we may remove this later on start */}
      {/* TODO: Move this route to DashboardRoutes and rename DashboardRoutes component to CustomerPortalROutes÷ */}
      <Route path="customer-portal/dashboard" element={<DashboardRoutes />} />
      <Route path="customer-portal/delivery-orders" element={<DeliveryOrders />} />
      <Route path="customer-portal/recurring-orders" element={<RecurringOrderPage />} />
      <Route path="customer-portal/graph" element={<Graph />} />
      <Route path="customer-portal-configuration" element={<CustomerPortalConfiguration />} />
      {/* Replace with Tank monitor v2 */}
      {/*<Route path="tank_monitors/*" element={<TankMonitorRoutes />} />*/}
      <Route path="staff/*" element={<StaffRoutes />} />
      <Route path="dispatch/qr_codes/all" element={<QrCodeList />} />
      {/* we may remove this later on end */}
      <Route path="*" element={<PlaceholderRoutes />} />
      <Route path="shift_boards/*" element={<ShiftBoardRoutes />} />
      <Route path="pandabox-version" element={<PandaBoxVersion />} />
      <Route path="mobile-version" element={<MobileVersion />} />
      <Route path="pandabox-logs" element={<PandaBoxLogs />} />
      <Route path="degree-days/*" element={<DegreeDaysRoutes />} />
      <Route path="tank_monitors/*" element={<TankMonitorV2Routes />} />
    </Route>
  </Route>
);
